import type { UseQueryResult } from '@tanstack/react-query';
import type { Atom } from 'jotai';
import { atom } from 'jotai';

import { fileConfirmUploadAtom } from '../_graphql/mutationFileConfirmUpload';
import { fileDeleteMutationAtom } from '../_graphql/mutationFileDelete';
import { purchaseLoadFileCreateAtom } from '../_graphql/mutationPurchaseLoadFileCreate';
import { purchaseLoadPhotosQueryAtom } from '../_graphql/queryPurchaseLoadPhotos';
import { type TicketPhotoWithOptimistic } from '../types/scaleTickets';

import { $optimisticPhotoUpdates, filesWithUrlsAtom } from './files.state';
import { $scaleTicketId } from './scaleTicketPro.state';

const $lazyTicketPhotosWithoutUrl = purchaseLoadPhotosQueryAtom((get) => ({
  variables: { id: get($scaleTicketId) ?? '' },
  lazy: true,
}));

export const $ticketPhotos = filesWithUrlsAtom((get) => ({
  variables: { id: get($scaleTicketId) ?? '' },
  skip: !get($scaleTicketId),
  photosAtom: $lazyTicketPhotosWithoutUrl,
}));

export const $ticketPhotosWithOptimistic = atom((get) => {
  const serverPhotosResult = get($ticketPhotos);
  const optimisticUpdates = get($optimisticPhotoUpdates);

  const serverPhotos = serverPhotosResult.data;
  if (!serverPhotos) {
    return {
      ...serverPhotosResult,
      data: undefined as TicketPhotoWithOptimistic[] | undefined,
    };
  }

  if (optimisticUpdates.size === 0) {
    return {
      ...serverPhotosResult,
      data: serverPhotos.map(
        (photo): TicketPhotoWithOptimistic => ({
          ...photo,
          _optimistic: undefined,
        }),
      ),
    };
  }

  const mergedPhotos = serverPhotos
    .filter((photo) => {
      const update = optimisticUpdates.get(photo.id);
      return !(update?.type === 'delete' && update.status === 'pending');
    })
    .map((photo): TicketPhotoWithOptimistic => {
      const update = optimisticUpdates.get(photo.id);
      if (update) {
        return {
          ...photo,
          ...(update.type === 'update' && update.status === 'pending'
            ? update.data
            : {}),
          _optimistic: {
            type: update.type,
            status: update.status,
            error: update.error,
          },
        };
      }
      return {
        ...photo,
        _optimistic: undefined,
      };
    });

  return {
    ...serverPhotosResult,
    data: mergedPhotos,
  };
}) as unknown as Atom<UseQueryResult<TicketPhotoWithOptimistic[], Error>>;

export const $createTicketFile = purchaseLoadFileCreateAtom();

export const $confirmTicketFileUpload = fileConfirmUploadAtom();

export const $deleteTicketFile = fileDeleteMutationAtom({
  onSuccess: (get) => {
    get($ticketPhotos).refetch();
  },
});
